<template>
  <section class="ai-practice-page">
    <header class="hero-card">
      <button type="button" class="back-btn" @click="goBack">{{ t("back") }}</button>

      <div class="hero-copy">
        <p class="hero-eyebrow">AI ROOT PRACTICE</p>
        <h1 class="hero-title">{{ t("title") }}</h1>
        <p class="hero-lead">{{ t("lead") }}</p>
      </div>

      <div class="language-box">
        <p class="language-label">{{ t("languageLabel") }}</p>
        <div class="language-list">
          <button
            v-for="choice in languageChoices"
            :key="choice.value"
            type="button"
            :disabled="languageLocked || busy || isTranscribing"
            :class="['lang-chip', { active: practiceLanguage === choice.value }]"
            @click="practiceLanguage = choice.value"
          >
            {{ choice.label }}
          </button>
        </div>
      </div>
    </header>

    <section class="content-grid">
      <article class="status-card">
        <div class="section-head">
          <span class="panel-tag">{{ t("statusTag") }}</span>
          <h2 class="section-title">{{ t("statusTitle") }}</h2>
        </div>

        <div class="status-list">
          <div class="status-item"><strong>{{ t("rootProgress") }}</strong><span>{{ rootProgressText }}</span></div>
          <div class="status-item"><strong>{{ t("wordProgress") }}</strong><span>{{ wordProgressText }}</span></div>
          <div class="status-item"><strong>{{ t("step") }}</strong><span>{{ currentStepText }}</span></div>
          <div class="status-item"><strong>{{ t("attempt") }}</strong><span>{{ attemptText }}</span></div>
          <div class="status-item"><strong>{{ t("tokenBudget") }}</strong><span>{{ tokenText }}</span></div>
          <div class="status-item"><strong>{{ t("audioBudget") }}</strong><span>{{ audioBudgetText }}</span></div>
          <div class="status-item"><strong>{{ t("aiJudge") }}</strong><span>{{ aiStateText }}</span></div>
          <div class="status-item"><strong>{{ t("voiceInput") }}</strong><span>{{ voiceInputText }}</span></div>
          <div class="status-item"><strong>{{ t("voiceOutput") }}</strong><span>{{ voiceOutputText }}</span></div>
        </div>

        <div class="action-stack">
          <button type="button" class="primary-btn" :disabled="busy || isTranscribing" @click="startSession">
            {{ session ? t("restart") : t("start") }}
          </button>
        </div>

        <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      </article>

      <article class="chat-card">
        <div class="chat-header">
          <div>
            <span class="panel-tag">{{ t("assistantTag") }}</span>
            <h2 class="section-title">{{ t("assistantTitle") }}</h2>
          </div>
          <div v-if="session?.question" class="question-chip">
            {{ session.question.wordText || session.question.rootText || t("waiting") }}
          </div>
        </div>

        <template v-if="!session">
          <div v-if="preview" class="preview-panel">
            <div class="voice-status-card">
              <strong>{{ t("previewTitle") }}</strong>
              <p>{{ t("previewLead") }}</p>
            </div>

            <div class="preview-grid">
              <div class="preview-block">
                <strong>{{ t("previewRoot") }}</strong>
                <p>{{ preview.rootText || "-" }}</p>
                <p>{{ preview.rootMeaning || "-" }}</p>
              </div>

              <div class="preview-block">
                <strong>{{ t("previewWord") }}</strong>
                <p>{{ preview.wordText || "-" }}</p>
              </div>

              <div class="preview-block" v-if="preview.parts?.length">
                <strong>{{ t("previewParts") }}</strong>
                <div class="preview-part-list">
                  <p v-for="(part, index) in preview.parts" :key="`${part.text}-${index}`">
                    <span>{{ part.text }}</span>
                    <span>{{ part.meaning }}</span>
                  </p>
                </div>
              </div>

              <div class="preview-block" v-if="preview.literalMeaning">
                <strong>{{ t("previewLiteral") }}</strong>
                <p>{{ preview.literalMeaning }}</p>
              </div>

              <div class="preview-block" v-if="preview.actualMeaning">
                <strong>{{ t("previewActual") }}</strong>
                <p>{{ preview.actualMeaning }}</p>
              </div>

              <div class="preview-block" v-if="preview.exampleSentence">
                <strong>{{ t("previewExample") }}</strong>
                <p>{{ preview.exampleSentence }}</p>
              </div>
            </div>

            <div class="preview-actions">
              <button type="button" class="primary-btn" :disabled="busy || isTranscribing || previewBusy" @click="startSession">
                {{ t("previewStart") }}
              </button>
            </div>
          </div>

          <div v-else class="empty-state">
            {{ previewBusy ? t("previewLoading") : t("previewEmpty") }}
          </div>
        </template>

        <template v-else>
          <template v-if="activeWordPreview">
            <div class="preview-panel">
              <div class="voice-status-card">
                <strong>{{ t("previewTitle") }}</strong>
                <p>{{ t("previewLead") }}</p>
              </div>

              <div class="preview-grid">
                <div class="preview-block">
                  <strong>{{ t("previewRoot") }}</strong>
                  <p>{{ activeWordPreview.rootText || "-" }}</p>
                  <p>{{ activeWordPreview.rootMeaning || "-" }}</p>
                </div>

                <div class="preview-block">
                  <strong>{{ t("previewWord") }}</strong>
                  <p>{{ activeWordPreview.wordText || "-" }}</p>
                </div>

                <div class="preview-block" v-if="activeWordPreview.parts?.length">
                  <strong>{{ t("previewParts") }}</strong>
                  <div class="preview-part-list">
                    <p v-for="(part, index) in activeWordPreview.parts" :key="`${part.text}-${index}`">
                      <span>{{ part.text }}</span>
                      <span>{{ part.meaning }}</span>
                    </p>
                  </div>
                </div>

                <div class="preview-block" v-if="activeWordPreview.literalMeaning">
                  <strong>{{ t("previewLiteral") }}</strong>
                  <p>{{ activeWordPreview.literalMeaning }}</p>
                </div>

                <div class="preview-block" v-if="activeWordPreview.actualMeaning">
                  <strong>{{ t("previewActual") }}</strong>
                  <p>{{ activeWordPreview.actualMeaning }}</p>
                </div>

                <div class="preview-block" v-if="activeWordPreview.exampleSentence">
                  <strong>{{ t("previewExample") }}</strong>
                  <p>{{ activeWordPreview.exampleSentence }}</p>
                </div>
              </div>

              <div class="preview-actions">
                <button type="button" class="primary-btn" :disabled="busy || isTranscribing" @click="startCurrentWord">
                  {{ t("previewStartWord") }}
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="message-list">
              <article
                v-for="(message, index) in messages"
                :key="`${message.role}-${index}`"
                :class="['message-bubble', message.role]"
              >
                <span class="message-role">{{ message.role === 'assistant' ? t("assistantName") : t("you") }}</span>
                <p>{{ message.text }}</p>
              </article>

              <div v-if="!messages.length" class="empty-state">
                {{ t("emptyState") }}
              </div>
            </div>

            <div v-if="session?.question" class="question-panel">
              <strong>{{ session.question.prompt }}</strong>
              <p v-if="session.question.helper">{{ session.question.helper }}</p>
            </div>

            <div class="voice-panel">
              <div class="voice-status-card">
                <strong>{{ t("transcriptTitle") }}</strong>
                <p>{{ transcriptPreview || t("transcriptEmpty") }}</p>
              </div>

              <div class="voice-toolbar">
                <button
                  type="button"
                  class="secondary-btn"
                  :disabled="!session || !voiceOutputSupported || !lastAssistantText"
                  @click="repeatAssistant"
                >
                  {{ t("repeatAssistant") }}
                </button>

                <button
                  type="button"
                  class="secondary-btn"
                  :disabled="!voiceOutputSupported"
                  @click="toggleVoiceOutput"
                >
                  {{ voiceOutputEnabled ? t("voiceOn") : t("voiceOff") }}
                </button>
              </div>

              <div class="voice-toolbar primary-row">
                <button
                  v-if="!isListening"
                  type="button"
                  class="voice-btn"
                  :disabled="!canUseVoiceInput"
                  @click="startListening"
                >
                  {{ t("startListening") }}
                </button>

                <button
                  v-else
                  type="button"
                  class="voice-btn stop"
                  @click="stopListening"
                >
                  {{ t("stopListening") }}
                </button>
              </div>

              <p class="voice-hint">{{ voiceHintText }}</p>
              <p v-if="voiceErrorMessage" class="error-text">{{ voiceErrorMessage }}</p>
            </div>
          </template>
        </template>
      </article>
    </section>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { API_BASE, ensureApiObject } from "./englishStudyShared";

const router = useRouter();
const MAX_RECORDING_MS = 20000;

const languageChoices = [
  { value: "ja", label: "日本語" },
  { value: "zh", label: "中文" },
  { value: "en", label: "English" },
];

const practiceLanguage = ref("ja");
const preview = ref(null);
const previewBusy = ref(false);
const activeWordPreview = ref(null);
const queuedAssistantPayload = ref(null);
const lastPreviewWordText = ref("");
const session = ref(null);
const messages = ref([]);
const busy = ref(false);
const isListening = ref(false);
const isTranscribing = ref(false);
const isSpeaking = ref(false);
const errorMessage = ref("");
const voiceErrorMessage = ref("");
const transcriptPreview = ref("");
const voiceOutputEnabled = ref(true);

let mediaRecorder = null;
let mediaStream = null;
let audioChunks = [];
let recordMimeType = "";
let stopTimer = null;
let currentAudio = null;
let currentAudioUrl = "";

const languageLocked = computed(() => !!session.value?.languageLocked);
const voiceInputSupported = computed(
  () =>
    typeof window !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== "undefined",
);
const voiceOutputSupported = computed(() => typeof window !== "undefined" && typeof window.Audio !== "undefined");
const lastAssistantText = computed(() => {
  for (let i = messages.value.length - 1; i >= 0; i -= 1) {
    if (messages.value[i]?.role === "assistant") {
      return messages.value[i].text || "";
    }
  }
  return "";
});
const rootProgressText = computed(() => {
  if (!session.value) {
    return "-";
  }
  const rootIndex = Number(session.value.currentRootIndex || 0);
  const rootCount = Number(session.value.rootCount || 0);
  const suffix = session.value.currentRootText ? ` (${session.value.currentRootText})` : "";
  return `${rootIndex} / ${rootCount}${suffix}`;
});
const wordProgressText = computed(() => {
  if (!session.value || !session.value.wordCountInRoot) {
    return "-";
  }
  const wordIndex = Number(session.value.currentWordIndex || 0);
  const wordCount = Number(session.value.wordCountInRoot || 0);
  const suffix = session.value.currentWordText ? ` (${session.value.currentWordText})` : "";
  return `${wordIndex} / ${wordCount}${suffix}`;
});
const currentStepText = computed(() => stepLabel(session.value?.currentStep || ""));
const attemptText = computed(() => (!session.value ? "-" : `${session.value.attempt || 0} / ${session.value.maxAttempts || 0}`));
const tokenText = computed(() => (!session.value ? "-" : `${session.value.tokenUsed || 0} / ${session.value.tokenLimit || 0}`));
const audioBudgetText = computed(() => (!session.value ? "-" : `${session.value.audioRequestUsed || 0} / ${session.value.audioRequestLimit || 0}`));
const aiStateText = computed(() => {
  if (!session.value) {
    return "-";
  }
  if (session.value.aiBudgetExceeded) {
    return t("aiBudgetExceeded");
  }
  return session.value.aiConfigured ? t("aiAlways") : t("aiDisabled");
});
const canUseVoiceInput = computed(
  () =>
    !!session.value
    && session.value.status !== "completed"
    && !activeWordPreview.value
    && !busy.value
    && !isTranscribing.value
    && !isListening.value
    && voiceInputSupported.value,
);
const voiceInputText = computed(() => {
  if (!voiceInputSupported.value) {
    return t("voiceUnsupported");
  }
  if (isTranscribing.value) {
    return t("transcribing");
  }
  if (isListening.value) {
    return t("recording");
  }
  return t("voiceReady");
});
const voiceOutputText = computed(() => {
  if (!voiceOutputSupported.value) {
    return t("voiceUnsupported");
  }
  if (isSpeaking.value) {
    return t("speaking");
  }
  return voiceOutputEnabled.value ? t("voiceOn") : t("voiceOff");
});
const voiceHintText = computed(() => {
  if (!session.value?.question) {
    return t("voiceHintIdle");
  }
  if (!voiceInputSupported.value) {
    return t("voiceHintUnsupported");
  }
  if (isTranscribing.value) {
    return t("voiceHintTranscribing");
  }
  if (session.value.question.step === "sentence") {
    return t("voiceHintSentence");
  }
  return t("voiceHintNormal");
});

const i18n = {
  ja: {
    back: "Back",
    title: "AI語根練習",
    lead: "語根と単語を、音声で AI とやり取りしながら順番に練習します。",
    languageLabel: "練習言語",
    statusTag: "STATUS",
    statusTitle: "進み具合",
    rootProgress: "語根",
    wordProgress: "単語",
    step: "ステップ",
    attempt: "試行",
    tokenBudget: "AI予算",
    audioBudget: "音声予算",
    aiJudge: "AI判定",
    voiceInput: "音声入力",
    voiceOutput: "音声出力",
    start: "開始",
    restart: "最初からやり直し",
    assistantTag: "ASSISTANT",
    assistantTitle: "小助手",
    assistantName: "小助手",
    you: "あなた",
    emptyState: "セッションを始めると会話がここに表示されます。",
    waiting: "待機中",
    aiAlways: "毎回AI判定",
    aiDisabled: "未設定",
    aiBudgetExceeded: "AI予算上限",
    transcriptTitle: "転写結果",
    transcriptEmpty: "まだ音声入力はありません。",
    repeatAssistant: "もう一度読む",
    voiceOn: "音声ON",
    voiceOff: "音声OFF",
    startListening: "話し始める",
    stopListening: "録音を止める",
    recording: "録音中",
    transcribing: "転写中",
    speaking: "再生中",
    voiceReady: "準備OK",
    voiceUnsupported: "未対応",
    voiceHintIdle: "まずセッションを開始してください。",
    voiceHintUnsupported: "このブラウザは録音に対応していません。Chrome または Edge をおすすめします。",
    voiceHintTranscribing: "OpenAI で音声を文字にしています。少し待ってください。",
    voiceHintNormal: "ボタンを押して答えを話してください。録音を止めると自動で送信されます。",
    voiceHintSentence: "このステップは英語で話してください。録音後に自動で転写して送信します。",
    errorStart: "セッションの開始に失敗しました。",
    errorReply: "回答の送信に失敗しました。",
    errorTranscribe: "音声の転写に失敗しました。",
    errorSpeech: "音声の再生に失敗しました。",
    errorMicDenied: "マイクの使用が拒否されました。ブラウザで許可してください。",
    errorMicEmpty: "録音できませんでした。もう一度試してください。",
    errorAudioBusy: "いまは音声を送れません。少し待ってから試してください。",
    errorPlaybackBlocked: "ブラウザに自動再生を止められました。再生ボタンを押してください。",
    stepRoot: "語根の意味",
    stepPart: "語の部分の意味",
    stepLiteral: "字面の意味",
    stepActual: "実際の意味",
    stepSentence: "英文作成",
  },
  zh: {
    back: "Back",
    title: "AI词根练习",
    lead: "用语音和 AI 一问一答，按顺序练习词根和单词。",
    languageLabel: "练习语言",
    statusTag: "STATUS",
    statusTitle: "进度",
    rootProgress: "词根",
    wordProgress: "单词",
    step: "步骤",
    attempt: "尝试",
    tokenBudget: "AI预算",
    audioBudget: "语音预算",
    aiJudge: "AI判定",
    voiceInput: "语音输入",
    voiceOutput: "语音输出",
    start: "开始",
    restart: "从头开始",
    assistantTag: "ASSISTANT",
    assistantTitle: "小助手",
    assistantName: "小助手",
    you: "你",
    emptyState: "开始会话后，对话会显示在这里。",
    waiting: "等待中",
    aiAlways: "每轮都连AI",
    aiDisabled: "未配置",
    aiBudgetExceeded: "AI预算用尽",
    transcriptTitle: "转写结果",
    transcriptEmpty: "还没有语音输入。",
    repeatAssistant: "再读一次",
    voiceOn: "语音开启",
    voiceOff: "语音关闭",
    startListening: "开始说话",
    stopListening: "停止录音",
    recording: "录音中",
    transcribing: "转写中",
    speaking: "播放中",
    voiceReady: "已准备",
    voiceUnsupported: "不支持",
    voiceHintIdle: "请先开始会话。",
    voiceHintUnsupported: "当前浏览器不支持录音，建议使用 Chrome 或 Edge。",
    voiceHintTranscribing: "正在用 OpenAI 转写语音，请稍等。",
    voiceHintNormal: "按下按钮后直接说答案，停止录音后会自动发送。",
    voiceHintSentence: "这一步请用英语回答。录音后会自动转写并发送。",
    errorStart: "开始会话失败。",
    errorReply: "发送回答失败。",
    errorTranscribe: "语音转写失败。",
    errorSpeech: "语音播放失败。",
    errorMicDenied: "麦克风权限被拒绝了，请在浏览器里允许访问。",
    errorMicEmpty: "没有录到清晰语音，请再试一次。",
    errorAudioBusy: "现在不能发送语音，请稍后再试。",
    errorPlaybackBlocked: "浏览器阻止了自动播放，请手动点一次播放。",
    stepRoot: "词根意思",
    stepPart: "词块意思",
    stepLiteral: "字面意思",
    stepActual: "实际意思",
    stepSentence: "英文造句",
  },
  en: {
    back: "Back",
    title: "AI Root Practice",
    lead: "Work through roots and words by talking with the assistant instead of typing.",
    languageLabel: "Practice Language",
    statusTag: "STATUS",
    statusTitle: "Progress",
    rootProgress: "Root",
    wordProgress: "Word",
    step: "Step",
    attempt: "Attempt",
    tokenBudget: "AI Budget",
    audioBudget: "Audio Budget",
    aiJudge: "AI Judge",
    voiceInput: "Voice Input",
    voiceOutput: "Voice Output",
    start: "Start Session",
    restart: "Restart from the top",
    assistantTag: "ASSISTANT",
    assistantTitle: "Helper",
    assistantName: "Helper",
    you: "You",
    emptyState: "Start a session and the conversation will appear here.",
    waiting: "Waiting",
    aiAlways: "Every reply uses AI",
    aiDisabled: "Not configured",
    aiBudgetExceeded: "Budget reached",
    previewTitle: "Preview Card",
    previewLead: "Review the entry first. These hints disappear once practice starts.",
    previewRoot: "Root",
    previewWord: "Word",
    previewParts: "Parts",
    previewLiteral: "Literal meaning",
    previewActual: "Actual meaning",
    previewExample: "Example sentence",
    previewEmpty: "No preview is available for this entry yet.",
    previewLoading: "Loading the preview...",
    previewLoadError: "Failed to load the preview card.",
    previewStart: "Start Practice",
    previewStartWord: "Start This Word",
    transcriptTitle: "Transcript",
    transcriptEmpty: "No voice input yet.",
    repeatAssistant: "Repeat",
    voiceOn: "Voice On",
    voiceOff: "Voice Off",
    startListening: "Start Talking",
    stopListening: "Stop Recording",
    recording: "Recording",
    transcribing: "Transcribing",
    speaking: "Speaking",
    voiceReady: "Ready",
    voiceUnsupported: "Unsupported",
    voiceHintIdle: "Start a session first.",
    voiceHintUnsupported: "This browser does not support microphone recording. Chrome or Edge is recommended.",
    voiceHintTranscribing: "OpenAI is transcribing your audio now. Please wait a moment.",
    voiceHintNormal: "Press the button, speak your answer, then stop recording to send it automatically.",
    voiceHintSentence: "For this step, answer in English. Your recording will be transcribed and sent automatically.",
    errorStart: "Failed to start the session.",
    errorReply: "Failed to send your reply.",
    errorTranscribe: "Failed to transcribe the recording.",
    errorSpeech: "Failed to play the assistant voice.",
    errorMicDenied: "Microphone access was denied. Please allow it in the browser.",
    errorMicEmpty: "No clear audio was captured. Please try again.",
    errorAudioBusy: "Audio is busy right now. Please wait a moment and try again.",
    errorPlaybackBlocked: "The browser blocked autoplay. Please press Repeat once.",
    stepRoot: "Root meaning",
    stepPart: "Part meaning",
    stepLiteral: "Literal meaning",
    stepActual: "Actual meaning",
    stepSentence: "Sentence",
  },
};

function t(key) {
  return i18n[practiceLanguage.value]?.[key] || i18n.en[key] || key;
}

function stepLabel(step) {
  if (step === "root_meaning") return t("stepRoot");
  if (step === "part_meaning") return t("stepPart");
  if (step === "literal_meaning") return t("stepLiteral");
  if (step === "actual_meaning") return t("stepActual");
  if (step === "sentence") return t("stepSentence");
  return "-";
}

function extractAxiosMessage(error, fallback) {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object" && typeof responseData.error === "string" && responseData.error.trim()) {
    return responseData.error.trim();
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

function pickRecordingMimeType() {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];

  return candidates.find((value) => window.MediaRecorder.isTypeSupported(value)) || "";
}

function guessAudioExtension(mimeType) {
  const normalized = String(mimeType || "").toLowerCase();
  if (normalized.includes("mp4")) return "m4a";
  if (normalized.includes("ogg")) return "ogg";
  return "webm";
}

function stopPlayback() {
  isSpeaking.value = false;
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }
  if (currentAudioUrl) {
    URL.revokeObjectURL(currentAudioUrl);
    currentAudioUrl = "";
  }
}

function cleanupRecorder() {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  if (mediaRecorder) {
    mediaRecorder.ondataavailable = null;
    mediaRecorder.onerror = null;
    mediaRecorder.onstart = null;
    mediaRecorder.onstop = null;
    mediaRecorder = null;
  }

  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }

  audioChunks = [];
  recordMimeType = "";
}

async function playAssistantAudio() {
  if (!voiceOutputSupported.value || !voiceOutputEnabled.value || !session.value?.sessionId || !lastAssistantText.value) {
    return;
  }

  stopPlayback();
  voiceErrorMessage.value = "";

  try {
    const response = await axios.post(
      `${API_BASE}/api/english/ai-practice/session/${session.value.sessionId}/assistant-audio`,
      {},
      {
        responseType: "blob",
        timeout: 30000,
      },
    );

    const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data], { type: "audio/mpeg" });
    currentAudioUrl = URL.createObjectURL(blob);
    currentAudio = new Audio(currentAudioUrl);
    currentAudio.onended = () => {
      isSpeaking.value = false;
    };
    currentAudio.onerror = () => {
      isSpeaking.value = false;
      voiceErrorMessage.value = t("errorSpeech");
    };
    isSpeaking.value = true;
    await currentAudio.play();
  } catch (error) {
    isSpeaking.value = false;
    const message = error?.name === "NotAllowedError"
      ? t("errorPlaybackBlocked")
      : extractAxiosMessage(error, t("errorSpeech"));
    voiceErrorMessage.value = message;
  }
}

function appendAssistantMessage(payload) {
  const text = payload?.assistantMessage?.text;
  if (!text) {
    return;
  }

  messages.value.push({ role: "assistant", text });
  void playAssistantAudio();
}

function toggleVoiceOutput() {
  voiceOutputEnabled.value = !voiceOutputEnabled.value;
  if (!voiceOutputEnabled.value) {
    stopPlayback();
  }
}

function repeatAssistant() {
  void playAssistantAudio();
}

async function startSession() {
  busy.value = true;
  errorMessage.value = "";
  voiceErrorMessage.value = "";
  transcriptPreview.value = "";
  stopPlayback();
  cleanupRecorder();

  try {
    const response = await axios.post(
      `${API_BASE}/api/english/ai-practice/session`,
      { language: practiceLanguage.value },
      { timeout: 15000 },
    );
    const payload = ensureApiObject(response, "AI root practice session");
    session.value = payload;
    messages.value = [];
    activeWordPreview.value = null;
    queuedAssistantPayload.value = null;
    appendAssistantMessage(payload);
  } catch (error) {
    console.error(error);
    errorMessage.value = extractAxiosMessage(error, t("errorStart"));
  } finally {
    busy.value = false;
  }
}

async function loadPreview() {
  if (session.value) {
    return;
  }

  previewBusy.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/english/ai-practice/preview`, {
      params: { language: practiceLanguage.value },
      timeout: 15000,
    });
    preview.value = ensureApiObject(response, "AI root practice preview");
    lastPreviewWordText.value = preview.value?.wordText || "";
  } catch (error) {
    console.error(error);
    preview.value = null;
    errorMessage.value = extractAxiosMessage(error, t("previewLoadError"));
  } finally {
    previewBusy.value = false;
  }
}

async function loadSessionPreview(sessionId) {
  const response = await axios.get(`${API_BASE}/api/english/ai-practice/session/${sessionId}/preview`, {
    timeout: 15000,
  });
  return ensureApiObject(response, "AI root practice session preview");
}

function shouldPauseForNextWord(previousWord, payload) {
  const normalizedPreviousWord = String(previousWord || "").trim();
  const nextWord = String(payload?.currentWordText || "").trim();
  if (!nextWord || !normalizedPreviousWord) {
    return false;
  }

  return nextWord !== normalizedPreviousWord && nextWord !== String(lastPreviewWordText.value || "").trim();
}

function startCurrentWord() {
  if (!queuedAssistantPayload.value) {
    activeWordPreview.value = null;
    return;
  }

  const payload = queuedAssistantPayload.value;
  queuedAssistantPayload.value = null;
  activeWordPreview.value = null;
  lastPreviewWordText.value = payload?.currentWordText || lastPreviewWordText.value;
  appendAssistantMessage(payload);
}

async function sendReply(answerText) {
  if (!session.value?.sessionId || !String(answerText || "").trim()) {
    return;
  }

  const answer = String(answerText || "").trim();
  const previousWord = session.value?.currentWordText || "";
  busy.value = true;
  errorMessage.value = "";
  voiceErrorMessage.value = "";
  messages.value.push({ role: "user", text: answer });
  transcriptPreview.value = answer;

  try {
    const response = await axios.post(
      `${API_BASE}/api/english/ai-practice/session/${session.value.sessionId}/reply`,
      { answer },
      { timeout: 30000 },
    );
    const payload = ensureApiObject(response, "AI root practice reply");
    session.value = payload;
    transcriptPreview.value = "";
    if (shouldPauseForNextWord(previousWord, payload)) {
      try {
        activeWordPreview.value = await loadSessionPreview(payload.sessionId);
        queuedAssistantPayload.value = payload;
        lastPreviewWordText.value = activeWordPreview.value?.wordText || lastPreviewWordText.value;
      } catch (previewError) {
        console.error(previewError);
        activeWordPreview.value = null;
        queuedAssistantPayload.value = null;
        appendAssistantMessage(payload);
      }
    } else {
      appendAssistantMessage(payload);
    }
  } catch (error) {
    console.error(error);
    errorMessage.value = extractAxiosMessage(error, t("errorReply"));
  } finally {
    busy.value = false;
  }
}

async function transcribeAndSend(blob) {
  if (!session.value?.sessionId) {
    return;
  }

  isTranscribing.value = true;
  voiceErrorMessage.value = "";
  transcriptPreview.value = t("transcribing");

  try {
    const formData = new FormData();
    const extension = guessAudioExtension(recordMimeType);
    formData.append("file", blob, `ai-root-practice.${extension}`);

    const response = await axios.post(
      `${API_BASE}/api/english/ai-practice/session/${session.value.sessionId}/transcribe`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      },
    );

    const payload = ensureApiObject(response, "AI root practice transcription");
    const text = String(payload.text || "").trim();
    if (!text) {
      voiceErrorMessage.value = t("errorMicEmpty");
      transcriptPreview.value = "";
      return;
    }

    transcriptPreview.value = text;
    await sendReply(text);
  } catch (error) {
    console.error(error);
    transcriptPreview.value = "";
    voiceErrorMessage.value = extractAxiosMessage(error, t("errorTranscribe"));
  } finally {
    isTranscribing.value = false;
  }
}

function stopListening() {
  if (mediaRecorder && mediaRecorder.state === "recording") {
    mediaRecorder.stop();
  }
}

async function startListening() {
  if (!canUseVoiceInput.value) {
    voiceErrorMessage.value = t("errorAudioBusy");
    return;
  }

  stopPlayback();
  voiceErrorMessage.value = "";
  errorMessage.value = "";
  transcriptPreview.value = "";

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordMimeType = pickRecordingMimeType();
    mediaRecorder = recordMimeType
      ? new MediaRecorder(mediaStream, { mimeType: recordMimeType })
      : new MediaRecorder(mediaStream);
    audioChunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };

    mediaRecorder.onstart = () => {
      isListening.value = true;
    };

    mediaRecorder.onerror = () => {
      isListening.value = false;
      voiceErrorMessage.value = t("errorMicEmpty");
      cleanupRecorder();
    };

    mediaRecorder.onstop = async () => {
      isListening.value = false;
      const mimeType = recordMimeType || "audio/webm";
      const blob = audioChunks.length > 0 ? new Blob(audioChunks, { type: mimeType }) : null;
      cleanupRecorder();

      if (!blob || blob.size <= 0) {
        voiceErrorMessage.value = t("errorMicEmpty");
        return;
      }

      await transcribeAndSend(blob);
    };

    mediaRecorder.start();
    stopTimer = window.setTimeout(() => {
      if (mediaRecorder && mediaRecorder.state === "recording") {
        mediaRecorder.stop();
      }
    }, MAX_RECORDING_MS);
  } catch (error) {
    console.error(error);
    cleanupRecorder();
    isListening.value = false;
    voiceErrorMessage.value = error?.name === "NotAllowedError"
      ? t("errorMicDenied")
      : extractAxiosMessage(error, t("errorMicEmpty"));
  }
}

function goBack() {
  router.push("/english/practice");
}

watch(practiceLanguage, () => {
  if (!session.value) {
    void loadPreview();
  }
});

onMounted(() => {
  void loadPreview();
});

onBeforeUnmount(() => {
  stopPlayback();
  cleanupRecorder();
});
</script>

<style scoped>
.ai-practice-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    linear-gradient(180deg, #f5f9ff 0%, #eef6fb 100%);
  color: #163047;
}

.hero-card,
.status-card,
.chat-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.hero-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.hero-eyebrow,
.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.hero-title,
.section-title {
  margin: 0;
  color: #10253a;
}

.hero-title {
  font-size: clamp(30px, 4vw, 52px);
}

.hero-lead,
.status-item span,
.question-panel p,
.voice-status-card p,
.voice-hint {
  color: #4c6377;
  line-height: 1.7;
}

.back-btn,
.primary-btn,
.secondary-btn,
.voice-btn,
.lang-chip {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.back-btn,
.primary-btn,
.secondary-btn,
.voice-btn {
  border: 0;
  border-radius: 14px;
  padding: 12px 16px;
  font-weight: 700;
}

.back-btn {
  background: #dceaf8;
  color: #133251;
}

.language-label {
  margin: 0 0 10px;
  color: #35648e;
  font-size: 12px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.language-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.lang-chip {
  border: 0;
  border-radius: 999px;
  padding: 10px 14px;
  background: #e8f0fa;
  color: #234666;
}

.lang-chip.active,
.primary-btn {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.secondary-btn {
  background: #e8f0fa;
  color: #163047;
}

.voice-btn {
  width: 100%;
  background: linear-gradient(135deg, #14532d, #22a06b);
  color: #fff;
}

.voice-btn.stop {
  background: linear-gradient(135deg, #9f1239, #e11d48);
}

.content-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 18px;
}

.status-card,
.chat-card {
  padding: 22px;
}

.status-list {
  display: grid;
  gap: 12px;
}

.status-item {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 18px;
  background: #f8fbff;
  border: 1px solid rgba(43, 90, 132, 0.12);
}

.status-item strong {
  color: #133251;
}

.action-stack {
  margin-top: 16px;
}

.primary-btn {
  width: 100%;
}

.error-text {
  margin: 12px 0 0;
  color: #b42318;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: start;
}

.question-chip {
  padding: 10px 14px;
  border-radius: 999px;
  background: #e8f0fa;
  color: #163047;
  font-weight: 700;
}

.message-list {
  display: grid;
  gap: 12px;
  margin-top: 18px;
  max-height: 360px;
  overflow: auto;
  padding-right: 4px;
}

.message-bubble {
  max-width: 82%;
  padding: 14px 16px;
  border-radius: 20px;
  background: #f5f9ff;
  border: 1px solid rgba(43, 90, 132, 0.12);
}

.message-bubble.assistant {
  justify-self: start;
}

.message-bubble.user {
  justify-self: end;
  background: #143d67;
  color: #fff;
}

.message-role {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.8;
}

.message-bubble p,
.empty-state {
  margin: 0;
  line-height: 1.7;
}

.question-panel,
.voice-status-card {
  margin-top: 18px;
  padding: 16px;
  border-radius: 20px;
  background: #f8fbff;
  border: 1px solid rgba(43, 90, 132, 0.12);
}

.question-panel strong,
.voice-status-card strong {
  color: #10253a;
}

.preview-panel {
  margin-top: 18px;
}

.preview-grid {
  margin-top: 18px;
  display: grid;
  gap: 12px;
}

.preview-block {
  padding: 16px;
  border-radius: 20px;
  background: #f8fbff;
  border: 1px solid rgba(43, 90, 132, 0.12);
}

.preview-block strong {
  display: block;
  margin-bottom: 8px;
  color: #10253a;
}

.preview-block p {
  margin: 0;
  color: #4c6377;
  line-height: 1.7;
}

.preview-part-list {
  display: grid;
  gap: 8px;
}

.preview-part-list p {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.preview-actions {
  margin-top: 18px;
}

.voice-panel {
  margin-top: 18px;
}

.voice-toolbar {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.voice-toolbar > button {
  flex: 1 1 200px;
}

.primary-row {
  margin-top: 16px;
}

.back-btn:hover,
.primary-btn:hover,
.secondary-btn:hover,
.voice-btn:hover,
.lang-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 1100px) {
  .hero-card,
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .ai-practice-page {
    padding: 12px;
  }

  .hero-card,
  .status-card,
  .chat-card {
    padding: 18px;
    border-radius: 22px;
  }

  .voice-toolbar {
    flex-direction: column;
  }

  .voice-toolbar > button {
    width: 100%;
  }
}
</style>
